import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link2, Copy, Mail, MessageCircle, Gift, Users as UsersIcon, CheckCircle2, Trophy, Award, Crown } from "lucide-react";

const BADGE_VISUAL = {
  bronze: { icon: Award, color: "#A16207", bg: "#FEF3C7", label: "Bronce", milestone: 5, bonus: "+1 mes extra" },
  silver: { icon: Award, color: "#475569", bg: "#F1F5F9", label: "Plata", milestone: 10, bonus: "+2 meses extra" },
  gold: { icon: Crown, color: "#CA8A04", bg: "#FEF9C3", label: "Oro", milestone: 20, bonus: "+3 meses extra" },
};

export default function Referrals() {
  const [me, setMe] = useState(null);
  const [list, setList] = useState([]);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setMe(r.data));
    api.get("/referrals/list").then((r) => setList(r.data));
    api.get("/referrals/leaderboard").then((r) => setBoard(r.data));
  }, []);

  if (!me) return <div className="text-slate-500">Cargando…</div>;

  const copyLink = () => { navigator.clipboard.writeText(me.referral_link); toast.success("Enlace copiado"); };
  const copyCode = () => { navigator.clipboard.writeText(me.referral_code); toast.success("Código copiado"); };
  const shareWa = () => {
    const msg = encodeURIComponent(`¡Hola! Te recomiendo AutoPresupuestos Pro para crear presupuestos en 30 segundos con IA. Si te registras con mi enlace nos llevamos 1 mes gratis los dos: ${me.referral_link}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };
  const shareEmail = () => {
    const subject = encodeURIComponent("Te recomiendo AutoPresupuestos Pro");
    const body = encodeURIComponent(`Hola,\n\nTe recomiendo AutoPresupuestos Pro para generar presupuestos profesionales en 30 segundos con IA.\n\nSi te registras con mi enlace, ambos recibimos 1 mes gratis: ${me.referral_link}\n\nUn saludo`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const next = me.next_milestone;
  const progress = next ? Math.min(100, (me.converted_count / next.count) * 100) : 100;

  return (
    <div data-testid="referrals-page" className="max-w-4xl">
      <div className="kbar">Programa de referidos</div>
      <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Invita y gana</h1>
      <p className="text-slate-500 mt-2">Por cada autónomo que se registre con tu enlace y se suscriba, <strong className="text-slate-900">recibís 1 mes gratis los dos</strong>. Y al alcanzar 5, 10 o 20 invitados convertidos, desbloqueas insignias con bonus extra.</p>

      {/* Hero share box */}
      <div className="mt-8 bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <Gift className="w-10 h-10 text-orange-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="kbar text-orange-600">Tu enlace personal</div>
            <div className="mt-2 bg-white border border-orange-200 px-4 py-3 font-mono text-sm break-all" data-testid="referral-link">{me.referral_link}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={copyLink} data-testid="copy-link" className="h-10 px-4 bg-white border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold"><Copy className="w-4 h-4" /> Copiar enlace</button>
              <button onClick={shareWa} data-testid="share-wa" className="h-10 px-4 inline-flex items-center gap-2 text-sm font-semibold text-white" style={{ background: "#25D366" }}><MessageCircle className="w-4 h-4" /> WhatsApp</button>
              <button onClick={shareEmail} data-testid="share-email" className="h-10 px-4 bg-white border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold"><Mail className="w-4 h-4" /> Email</button>
            </div>
            <div className="mt-4 text-sm text-slate-600">
              O comparte tu código: <button onClick={copyCode} data-testid="referral-code" className="font-mono font-bold text-orange-700 underline">{me.referral_code}</button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200 mt-6">
        <Stat icon={UsersIcon} label="Invitados" value={me.referees_count} testid="stat-referees" />
        <Stat icon={CheckCircle2} label="Convertidos" value={me.converted_count} sub="se suscribieron" testid="stat-converted" />
        <Stat icon={Gift} label="Meses gratis" value={me.credits_months} sub="acumulados" accent testid="stat-credits" />
      </div>

      {/* Badges + progress */}
      <div className="mt-px border border-slate-200 bg-white p-6" data-testid="badges-section">
        <div className="kbar mb-3">Insignias y bonus</div>
        <div className="grid grid-cols-3 gap-3">
          {["bronze", "silver", "gold"].map((b) => {
            const v = BADGE_VISUAL[b];
            const earned = me.badges?.includes(b);
            const Icon = v.icon;
            return (
              <div key={b} data-testid={`badge-${b}`} className={`p-4 border transition-all ${earned ? "border-current shadow-sm" : "border-slate-200 opacity-60"}`} style={earned ? { borderColor: v.color, background: v.bg } : {}}>
                <div className="flex items-start justify-between">
                  <Icon className="w-8 h-8" style={{ color: earned ? v.color : "#94A3B8" }} />
                  {earned && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 text-white" style={{ background: v.color }}>Ganada ✓</span>}
                </div>
                <div className="mt-3 font-display font-extrabold text-lg" style={{ color: earned ? v.color : "#0F172A" }}>{v.label}</div>
                <div className="text-xs text-slate-600 mt-1">{v.milestone} convertidos</div>
                <div className="text-xs font-bold mt-1" style={{ color: earned ? v.color : "#475569" }}>{v.bonus}</div>
              </div>
            );
          })}
        </div>
        {next && (
          <div className="mt-5">
            <div className="flex items-baseline justify-between text-sm mb-1.5">
              <span className="text-slate-700">Próxima insignia: <strong>{BADGE_VISUAL[next.badge]?.label}</strong></span>
              <span className="text-slate-500 tabular-nums">{me.converted_count} / {next.count}</span>
            </div>
            <div className="h-2 bg-slate-100 overflow-hidden">
              <div className="h-full bg-orange-600 transition-all duration-700" style={{ width: `${progress}%` }} data-testid="next-progress" />
            </div>
            <div className="text-xs text-slate-500 mt-1">Te faltan <strong>{next.count - me.converted_count}</strong> conversiones para desbloquearla.</div>
          </div>
        )}
        {!next && (
          <div className="mt-5 p-4 bg-yellow-50 border border-yellow-200">
            <div className="flex items-center gap-2 text-yellow-800 font-bold"><Trophy className="w-5 h-5" /> ¡Has desbloqueado todas las insignias!</div>
            <div className="text-xs text-yellow-700 mt-1">Sigues acumulando 1 mes gratis por cada nuevo invitado convertido.</div>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      {board && (
        <div className="mt-px border border-slate-200 bg-white p-6" data-testid="leaderboard-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="kbar">Ranking del mes</div>
              <h2 className="font-display font-bold text-xl mt-1">Top 10 referidores · {board.month}</h2>
            </div>
            <Trophy className="w-7 h-7 text-yellow-500" />
          </div>
          {board.leaderboard.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">Aún no hay conversiones este mes. ¡Sé el primero!</p>
          ) : (
            <ol className="divide-y divide-slate-100 border border-slate-200">
              {board.leaderboard.map((row, i) => (
                <li key={row.user_id + i} data-testid={`leaderboard-row-${row.rank}`} className={`flex items-center justify-between p-3 ${row.is_me ? "bg-orange-50 border-l-4 border-l-orange-600" : ""}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`font-display font-extrabold text-lg w-8 text-center ${row.rank === 1 ? "text-yellow-500" : row.rank === 2 ? "text-slate-400" : row.rank === 3 ? "text-amber-700" : "text-slate-500"}`}>{row.rank === "—" ? "—" : `#${row.rank}`}</span>
                    <span className="font-semibold truncate">{row.name}{row.is_me && <span className="text-xs text-orange-600 ml-2">· tú</span>}</span>
                  </div>
                  <span className="text-sm font-bold tabular-nums">{row.conversions} convertido{row.conversions !== 1 ? "s" : ""}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* List */}
      <h2 className="font-display font-bold text-2xl mt-12 tracking-tight">Tus invitados</h2>
      <div className="mt-4 border border-slate-200 bg-white">
        {list.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Link2 className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3">Aún no has invitado a nadie. Comparte tu enlace y empieza a ganar meses gratis.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((u) => (
              <li key={u.id} className="px-5 py-4 flex items-center justify-between gap-3" data-testid={`referee-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{u.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.business_name || "—"}</div>
                </div>
                <div className="text-right">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${u.subscription_status === "active" ? "bg-green-100 text-green-700" : u.subscription_status === "trialing" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-700"}`}>
                    {u.subscription_status === "active" ? "Suscrito ✓" : u.subscription_status === "trialing" ? "Prueba" : u.subscription_status}
                  </span>
                  <div className="text-xs text-slate-400 mt-1">{u.created_at?.slice(0, 10)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className={`p-6 ${accent ? "bg-orange-600 text-white" : "bg-white"}`} data-testid={testid}>
      <Icon className={`w-6 h-6 ${accent ? "text-white/80" : "text-slate-400"}`} />
      <div className="mt-4 text-3xl font-display font-extrabold">{value}</div>
      <div className={`text-sm ${accent ? "text-white/90" : "text-slate-500"}`}>{label}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/80" : "text-slate-400"}`}>{sub}</div>}
    </div>
  );
}
